import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { interval, Subscription } from 'rxjs';
import { LatestEventService } from '../../services/latestEvent.service';

export interface LatestEvent {
  username: string;
  source: 'YouTube' | 'Twitch';
  event_type: 'Follow' | 'Sub' | 'ReSub' | 'GiftSub' | 'NewSponsor' | 'GiftMembershipReceived';
  event_id: number;
}

@Component({
  selector: 'app-latest-event-overlay',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './LatestEventOverlay.component.html',
  styleUrls: ['./LatestEventOverlay.component.scss']
})
export class LatestEventOverlayComponent implements OnInit, OnDestroy {
  latestEvent: LatestEvent | null = null;
  isLoading = true;
  isPlayingAnimation = false;
  currentAnimationPath = '';
  private subscription: Subscription = new Subscription();
  private shownEventIds = new Set<number>();

  constructor(private latestEventService: LatestEventService) {}

  ngOnInit(): void {
    this.fetchLatestEvent();
      
    const intervalSubscription = interval(5000).subscribe(() => {
      this.fetchLatestEvent();
    });
    
    this.subscription.add(intervalSubscription);
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private fetchLatestEvent(): void {
    this.latestEventService.getLatestEvent().subscribe({
      next: (event) => {
        // Only process if event exists and has required properties
        if (event && event.username && event.event_id) {
          // Check if we've already shown this event
          if (!this.shownEventIds.has(event.event_id)) {
            this.playAnimationForEvent(event);
          }
        }
      },
      error: (error) => {
        console.error('Error fetching latest event:', error);
        // Do nothing on error - keep showing previous result
      }
    });
  }

  private playAnimationForEvent(event: LatestEvent): void {
    // Mark event as shown immediately to prevent duplicate animations
    this.shownEventIds.add(event.event_id);
    
    this.currentAnimationPath = this.getAnimationPath(event.event_type);
    this.isPlayingAnimation = true;
    this.isLoading = false;
    
    // Hide animation and show username after animation duration (5.166 seconds based on webm duration)
    setTimeout(() => {
      this.isPlayingAnimation = false;
      this.latestEvent = event;
      
      // Mark event as shown after animation completes
      this.latestEventService.markEventAsShown(event.event_id).subscribe({
        next: () => console.log('Event marked as shown'),
        error: (error) => console.error('Error marking event as shown:', error)
      });
    }, 5166);
  }

  private getAnimationPath(eventType: string): string {
    const animationMap: { [key: string]: string } = {
      'Follow': '/assets/eventsAnimations/alert twitch new follower cropped.webm',
      'Sub': '/assets/eventsAnimations/alert twitch subscriber cropped.webm',
      'ReSub': '/assets/eventsAnimations/alert twitch resub cropped.webm',
      'GiftSub': '/assets/eventsAnimations/alert twitch gifted member cropped.webm',
      'NewSponsor': '/assets/eventsAnimations/alert youtube new member cropped.webm',
      'GiftMembershipReceived': '/assets/eventsAnimations/alert youtube gifted member cropped.webm'
    };
    
    return animationMap[eventType] || '';
  }

  getPlatformIcon(source: string): string {
    return source === 'YouTube' ? '/youtube.svg' : '/twitch.svg';
  }
}
